
import React, {useState} from 'react';
import styles from './_finance-deferred-down-editor-dialog.module.scss';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid
} from "@mui/material";
import {DatePickerChangeEvent} from "@progress/kendo-react-dateinputs";
import {Error, Hint} from "@progress/kendo-react-labels";
import {
  FinanceQuote,
  getEnumForValue,
  getOptionsFromEnumAndLoanClass,
  hasValue, NoScrollNumericTextBox,
  DatePickerWithYearSanitizer
} from "@amsanalytics/react-shared-library";
import {ComboBox, ComboBoxChangeEvent} from "@progress/kendo-react-dropdowns";
import {NumericTextBoxChangeEvent} from "@progress/kendo-react-inputs";
import {ILookupItems} from "../../../../../Interfaces/ILookupItems";
import {DialogProps} from "../../../../Dialogs/DialogProps";
import {startOfDay} from "../../../../../utils/LoanCalcUtils";

export interface IGenericItemEditorError {
  message: string;
  canBeBypassed: boolean;
}

export interface DeferredDownInfo {
  startDate: Date | null;
  paymentAmount: number;
  numberOfPayments: number;
  paymentFrequency: number;
}

type SubmitButtonText = "Add" | "Update";
export type GenericEditorActionType = SubmitButtonText | "Bypass";

export interface FinanceDeferredDownEditorDialogProps extends DialogProps<void> {
  selectedFinanceQuote: FinanceQuote;
  submitButtonText: SubmitButtonText;
  onSubmit: (item: DeferredDownInfo) => void;
  onRemoveAll: () => void;
  lookups: ILookupItems;
  maxNumberOfPayments: number;
}

// Much of this was copied from the GenericItemEditorDialog
export default function FinanceDeferredDownEditorDialog(props: FinanceDeferredDownEditorDialogProps) {
  const financeQuoteDeferredPayments = props.selectedFinanceQuote.financeQuoteDeferredPayments;
  const thereWasAtLeastOneDownPaymentOnOpen = financeQuoteDeferredPayments.length > 0;
  const firstPaymentDate = thereWasAtLeastOneDownPaymentOnOpen
    ? new Date(Math.min(...financeQuoteDeferredPayments.map(dp => new Date(dp.paymentDate!).getTime())))
    : undefined;
  const paymentFrequencies = getOptionsFromEnumAndLoanClass(props.lookups.carletonItems, "PaymentFrequency", props.selectedFinanceQuote.financeLoanClassId);
  const startDateMin = startOfDay(new Date(props.selectedFinanceQuote.contractDate));
  const [deferredDownInfo, setDeferredDownInfo] = useState<DeferredDownInfo>({
      startDate: firstPaymentDate ?? null, 
      paymentAmount: thereWasAtLeastOneDownPaymentOnOpen
        ? financeQuoteDeferredPayments[0].amount ?? 0
        : 0,
      numberOfPayments: financeQuoteDeferredPayments.length === 0
        ? 1
        : financeQuoteDeferredPayments.length,
      paymentFrequency: props.selectedFinanceQuote.deferredDownPaymentPaymentFrequency
    });
  const [validatorErrors, setValidatorErrors] = useState<IGenericItemEditorError[]>([]);
  const errorsThatCanBeBypassed = validatorErrors.filter(e => e.canBeBypassed);
  const errorsThatCanNotBeBypassed = validatorErrors.filter(e => !e.canBeBypassed);
  const selectedPaymentFrequency = getEnumForValue(paymentFrequencies, deferredDownInfo.paymentFrequency);

  const onSubmit = (bypassTheErrorsThatCanBeBypassed: boolean, actionType: GenericEditorActionType) => {
    const errors: IGenericItemEditorError[] = [];
    const dateErrors = datesValidator();
    errors.push(...dateErrors);
    
    if (!hasValue(deferredDownInfo.startDate)) {
      errors.push({message: "Start Date is a required field", canBeBypassed: false});
    }
    
    if (deferredDownInfo.paymentAmount <= 0) {
      errors.push({message: "The payment amount has to be greater than 0", canBeBypassed: false})
    }
    
    if (deferredDownInfo.numberOfPayments <= 0) {
      errors.push({message: "The number of deferred payments has to be greater than 0", canBeBypassed: false})
    }

    if (deferredDownInfo.numberOfPayments > props.maxNumberOfPayments) {
      errors.push({message: `The number of deferred payments has to be less than ${props.maxNumberOfPayments}`, canBeBypassed: false})
    }

    setValidatorErrors(errors);
    
    const thereAreErrorsThatCanNotBeBypassed = errors.filter(e => !e.canBeBypassed).length > 0;
    const thereAreErrorsThatCanBeBypassed = errors.filter(e => e.canBeBypassed).length > 0;

    // It returns early IF 
    // 1. There are any errors that CAN'T be bypassed
    // 2. There are errors that CAN be bypassed, but the user didn't click the "Bypass" button
    if (thereAreErrorsThatCanNotBeBypassed || (thereAreErrorsThatCanBeBypassed && !bypassTheErrorsThatCanBeBypassed)) {
      return;
    }

    if (props.onSubmit) {
      props.onSubmit(deferredDownInfo);
    }
  }

  // Checks to make sure that the user didn't input "23" as the year. This will prompt them to input "2023" as the year.
  const datesValidator = (): IGenericItemEditorError[] => {
    const errors: IGenericItemEditorError[] = [];

    if (hasValue(deferredDownInfo.startDate) && new Date(deferredDownInfo.startDate!).getFullYear() < 1000) {
      errors.push({message: "The start date's year has to have 4 digits. Ex- instead of '23', enter '2023'.", canBeBypassed: false});
    }

    if (hasValue(deferredDownInfo.startDate) && startOfDay(new Date(deferredDownInfo.startDate!)).getTime() < startDateMin.getTime()) {
      errors.push({message: "The start date must be on or after the closing date.", canBeBypassed: false});
    }

    return errors;
  }
  
  return (
    <div className={styles.financeDeferredDownEditorDialogContainer}>
      <DialogTitle id="form-dialog-title">
        {props.title}
      </DialogTitle>
      <DialogContent>
        <div style={{display: 'flex', flexWrap: "wrap", flexDirection: 'column', padding: '10px', flex: 1}}>
          <DatePickerWithYearSanitizer
            label={"Start Date"}
            value={deferredDownInfo.startDate ? new Date(deferredDownInfo.startDate) : undefined}
            min={startDateMin}
            onChange={(event: DatePickerChangeEvent) => setDeferredDownInfo({...deferredDownInfo, startDate: event.value})}
            required={true}
          />
          <ComboBox
            style={{width: '100%'}}
            label={'Pay Frequency'}
            data={paymentFrequencies}
            textField="option"
            suggest={true}
            clearButton={false}
            value={selectedPaymentFrequency}
            onChange={(event: ComboBoxChangeEvent) => setDeferredDownInfo({...deferredDownInfo, paymentFrequency: event.value.value})}
          />
          <NoScrollNumericTextBox
            style={{width: '100%', marginLeft: '3px'}}
            valid={deferredDownInfo.numberOfPayments > 0}
            label={'Number of Payments'}
            value={deferredDownInfo.numberOfPayments}
            spinners={false}
            onChange={(e: NumericTextBoxChangeEvent) => setDeferredDownInfo({...deferredDownInfo, numberOfPayments: Number(e.target.value)})}
            step={1}
            min={1}
            max={6}
          />
          <NoScrollNumericTextBox
            style={{width: '100%', marginRight: '3px'}}
            label={'Amount'}
            value={deferredDownInfo.paymentAmount}
            spinners={false}
            format="c2"
            onChange={(e: NumericTextBoxChangeEvent) => {
              const value = e.value ?? 0;
              setDeferredDownInfo({...deferredDownInfo, paymentAmount: value});
            }}
            min={0}
          />
        {errorsThatCanNotBeBypassed.length > 0 &&
          <Grid item xs={12}>
            {errorsThatCanNotBeBypassed.map((error, key) => <Error key={key}>{error.message}</Error>)}
          </Grid>
        }
        {errorsThatCanBeBypassed.length > 0 &&
          <Grid item xs={12}>
            {errorsThatCanBeBypassed.map((error, key) => <Hint key={key}>{error.message}</Hint>)}
          </Grid>
        }
        </div>
        

        <DialogActions>
          {errorsThatCanBeBypassed.length > 0 && errorsThatCanNotBeBypassed.length === 0 &&
            <Button
              onClick={() => onSubmit(true, "Bypass")}
              color="error"
              variant="contained"
            >
              Bypass
            </Button>
          }
          <Button
            onClick={() => onSubmit(false, props.submitButtonText)}
            color="primary"
            variant="contained"
          >
            {props.submitButtonText}
          </Button>
          {thereWasAtLeastOneDownPaymentOnOpen &&
            <Button
              onClick={() => props.onRemoveAll()}
              color="error"
              variant="contained"
            >
              Remove
            </Button>
          }
          <Button
            autoFocus
            onClick={props.closeDialog}
            color="secondary"
            variant="contained"
          >
            Cancel
          </Button>
        </DialogActions>
      </DialogContent>
    </div>
  );
}
